<template>
  <div class="rmaForm">
    <div v-if="loading" class="loader loader-positioning" />
    <div>
      <p v-if="$store.state.needCustomTransport">
        <span
          v-html="$mlGet('rmaTransport3-header-customTransport-sizes')"
        ></span>
      </p>
      <p>
        <span v-html="$mlGet('rmaTransport3-header-info')"></span>
      </p>
    </div>
    <div id="boxImage-wrapper">
      <span class="floaty" id="length">{{
        $mlGet("rmaTransport3-picture-length")
      }}</span>
      <span class="floaty" id="width">{{
        $mlGet("rmaTransport3-picture-width")
      }}</span>
      <span class="floaty" id="height">{{
        $mlGet("rmaTransport3-picture-height")
      }}</span>
      <img id="boxImage" src="@/assets/kartonbox.png" alt="box-picture" />
    </div>
    <div id="input-grid">
      <label for="input-packageHeight">{{
        $mlGet("transport3-input-packageHeight")
      }}</label>
      <div class="input-wrapper">
        <input
          name="packageHeight"
          type="number"
          v-model="$store.state.rma.packageHeight"
          id="input-packageHeight"
        /><span>cm</span>
      </div>
      <ul v-if="heightErrors.length > 0" class="error-display">
        <li v-for="error in heightErrors" :key="error">{{ error }}</li>
      </ul>
      <label for="input-packageHeight">{{
        $mlGet("transport3-input-packageWidth")
      }}</label>
      <div class="input-wrapper">
        <input
          name="packageWidth"
          type="number"
          v-model="$store.state.rma.packageWidth"
          id="input-packageWidth"
        /><span>cm</span>
      </div>
      <ul v-if="widthErrors.length > 0" class="error-display">
        <li v-for="error in widthErrors" :key="error">{{ error }}</li>
      </ul>
      <label for="input-packageHeight">{{
        $mlGet("transport3-input-packageLength")
      }}</label>
      <div class="input-wrapper">
        <input
          name="packageLength"
          type="number"
          v-model="$store.state.rma.packageLength"
          id="input-packageLength"
        /><span>cm</span>
      </div>
      <ul v-if="lengthErrors.length > 0" class="error-display">
        <li v-for="error in lengthErrors" :key="error">{{ error }}</li>
      </ul>
      <label for="input-packageHeight">{{
        $mlGet("transport3-input-packageWeight")
      }}</label>
      <div class="input-wrapper">
        <input
          name="packageWeight"
          type="number"
          v-model="$store.state.rma.packageWeight"
          id="input-packageWeight"
        /><span>Kg</span>
      </div>
      <ul v-if="weightErrors.length > 0" class="error-display">
        <li v-for="error in weightErrors" :key="error">{{ error }}</li>
      </ul>
    </div>

    <div></div>
    <div class="information-box">
      <p>
        <span v-html="$mlGet('rmaTransport3-informationBox')"></span>
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { confirmTransport } from "@/helpers/transport";
import { PageChangeContext, PageName } from "@/views/CreateRma.vue";
import { Component, Vue } from "vue-property-decorator";

interface InputError {
  field: string;
  message: string;
}

@Component
export default class RmaTransport3 extends Vue {
  public loading = false;

  public errors: InputError[] = [];

  get heightErrors() {
    return this.errors
      .filter(error => error.field === "packageHeight")
      .map(error => error.message);
  }

  get widthErrors() {
    return this.errors
      .filter(error => error.field === "packageWidth")
      .map(error => error.message);
  }

  get lengthErrors() {
    return this.errors
      .filter(error => error.field === "packageLength")
      .map(error => error.message);
  }

  get weightErrors() {
    return this.errors
      .filter(error => error.field === "packageWeight")
      .map(error => error.message);
  }

  public async beforePageLeave(context: PageChangeContext): Promise<boolean> {
    // Ceil the values to whole numbers
    const { packageHeight, packageWidth, packageLength, packageWeight } = {
      packageHeight: Math.ceil(parseInt(this.$store.state.rma.packageHeight)),
      packageWidth: Math.ceil(parseInt(this.$store.state.rma.packageWidth)),
      packageLength: Math.ceil(parseInt(this.$store.state.rma.packageLength)),
      packageWeight: Math.ceil(parseInt(this.$store.state.rma.packageWeight))
    };

    // Store the values back to the store
    this.$store.state.rma.packageHeight = packageHeight ?? 0;
    this.$store.state.rma.packageWidth = packageWidth ?? 0;
    this.$store.state.rma.packageLength = packageLength ?? 0;
    this.$store.state.rma.packageWeight = packageWeight ?? 0;

    this.errors = [];

    const fields = {
      packageHeight,
      packageWidth,
      packageLength,
      packageWeight
    };

    for (const [field, value] of Object.entries(fields)) {
      if (isNaN(value)) {
        this.errors.push({
          field,
          message: this.$mlGet("transport3-error-generic")
        });
      }

      if (value <= 0) {
        this.errors.push({
          field,
          message: this.$mlGet("transport3-error-min")
        });
      }

      if (value > 160) {
        this.errors.push({
          field,
          message: this.$mlGet("transport3-error-max")
        });
      }
    }

    if (this.errors.length > 0) {
      return false;
    }

    if (context.isReturnOperation) {
      return true;
    }

    return await confirmTransport(loading => (this.loading = loading));
  }

  public async afterPageEnter(context: PageChangeContext): Promise<void> {
    return;
  }

  public async onPageLeave(context: PageChangeContext): Promise<PageName> {
    return "TransportServicepoints";
  }
}
</script>

<style scoped>
input {
  border: 1px solid lightgray;
  box-sizing: border-box;
  padding: 5px;
  margin: 0.4ex 1ex;
  width: 50%;
}

.input-wrapper {
  grid-column: 2 / 3;
}

#input-grid {
  display: grid;
  grid-template-columns: 26ex 14ex;
  width: 50%;
}

#input-grid label {
  margin: auto 0;
  grid-column: 1 / 2;
}

#boxImage-wrapper {
  float: right;
  margin: -10ex 10ex 0 0;
}

#boxImage {
  height: 20ex;
}

.floaty {
  position: relative;
  display: block;
}

#length {
  top: 25ex;
  left: 15ex;
}

#height {
  top: 10ex;
  left: 24ex;
}

#width {
  right: 20ex;
  top: 22ex;
  text-align: right;
}

button {
  font-family: overpass, arial;
  background-color: #da6f38 !important;
  border: none !important;
  font-size: 120%;
  font-weight: 400;
  border: none;
  padding: 8px 20px;
  margin: 2ex 0px;
  color: white;
  width: auto;
  cursor: pointer;
}

p {
  margin: 0;
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}

.error-display {
  color: #da6f38;
  list-style-type: none;
  padding: 0;
  margin: 0;
  margin-bottom: 0.5ex;
}
</style>
